/*
  Who is working on what, in plain words. A presence entry names a screen by its id (the heartbeat of 2.10);
  this file says what that screen is for.
*/

import type { EventNotice, Id, PresenceEntry } from "../../../api/types";

const SCREEN_WORDS: Record<string, string> = {
  home: "on the home screen",
  live_view: "in the live view",
  family_navigator: "in the family navigator",
  gallery: "in the gallery",
  whats_new: "reading what's new",
  new_session: "starting a session",
  live_map: "working in a session",
  session_log: "working in a session",
  checks_list: "working in a session",
  coverage_report: "working in a session",
  hand_over: "working on a hand-over",
  restore_control: "looking at the restore points",
  add_by_hand_form: "adding an element by hand",
  edit_element: "changing an element",
  element_page: "reading an element",
  symbol_picker: "picking symbols",
  upload_form: "adding a document",
  deliverable_view: "reading a document",
  library_browser: "searching the libraries",
  files_view: "looking at the files",
  chapter_editor: "writing a specification chapter",
  chapters: "looking at the specification chapters",
  project_lists: "looking at the project code lists",
  open_points: "looking at the open points",
  sme_inbox: "reviewing in the SME inbox",
  back_from_sme: "reading what came back from the SME",
  onboarding: "in getting started",
  my_profile: "on their profile",
};

/** "Sara is reading the page of Item A" style, without the name: "reading the page of Item A". */
export function describePresence(entry: PresenceEntry, itemName?: string | null): string {
  if (entry.screen === "item_page") return itemName ? `reading the page of ${itemName}` : "reading an item page";
  if (entry.screen === "item_map") return itemName ? `looking at the map of ${itemName}` : "looking at the map of an item";
  if ((entry.screen === "live_map" || entry.screen === "session_log" || entry.screen === "checks_list" || entry.screen === "coverage_report") && itemName) {
    return `working in a session on ${itemName}`;
  }
  return SCREEN_WORDS[entry.screen] ?? "online";
}

/** The others who are online: the person's own entry left out, the newest arrival last. */
export function othersOnline(entries: readonly PresenceEntry[], ownMembershipId: Id | null | undefined): PresenceEntry[] {
  return entries.filter((entry) => entry.membership_id !== ownMembershipId).sort((a, b) => a.since.localeCompare(b.since));
}

/** The events that only say the agent began or ended an answer: they tell an SME nothing about the ontology (UI-16). */
const AGENT_CHATTER: ReadonlySet<string> = new Set(["agent_turn_started", "agent_turn_ended"]);

/** The notices without the agent's starts and ends, for the SME's feed. */
export function withoutAgentChatter(notices: readonly EventNotice[]): EventNotice[] {
  return notices.filter((notice) => !AGENT_CHATTER.has(notice.type));
}

/**
 * The latest notices of the workspace for the live feed: the ones the server listed and the ones the live channel
 * has pushed since, once each, newest first.
 */
export function mergeFeed(listed: readonly EventNotice[], pushed: readonly EventNotice[], limit = 12): EventNotice[] {
  const byNumber = new Map<number, EventNotice>();
  for (const notice of [...listed, ...pushed]) if (!byNumber.has(notice.number)) byNumber.set(notice.number, notice);
  return Array.from(byNumber.values())
    .sort((a, b) => b.number - a.number)
    .slice(0, limit);
}

/** Where a notice leads in the family navigator: the first element it names. */
export function noticeTarget(notice: EventNotice): Id | null {
  return notice.element_ids[0] ?? null;
}
